import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Alert } from 'reactstrap';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { platformMeta } from '../platformMeta';
import { SocialPlatform } from '../types';

interface ComposerMediaWarningProps {
  /** The platforms that cannot post this text-only post. */
  platforms: SocialPlatform[];
  onAttach: () => void;
}

/**
 * Design product-gaps b1 and b2: a post with no media cannot go out on Instagram, TikTok or
 * YouTube. The alert names each such channel, and offers to attach a clip. The Schedule button
 * stays off while it shows.
 */
const ComposerMediaWarningComponent: React.FC<ComposerMediaWarningProps> = ({ platforms, onAttach }) => {
  const intl = useIntl();

  const names = useMemo(
    () =>
      intl.formatList(
        platforms.map((platform) => platformMeta[platform].name),
        { type: 'conjunction' }
      ),
    [intl, platforms]
  );

  return (
    <Alert color="danger" className="d-flex flex-wrap align-items-center justify-content-between gap-2">
      <span>
        <strong>
          {intl.formatMessage(
            {
              id: 'app.sml.promote.composer.needsMedia.title',
              defaultMessage: '{channels} cannot post text only.',
            },
            { channels: names }
          )}
        </strong>{' '}
        {intl.formatMessage({
          id: 'app.sml.promote.composer.needsMedia.body',
          defaultMessage: 'Attach a clip, or remove these channels from the post.',
        })}
      </span>
      <SMLButton outline omitCap onClick={onAttach}>
        {intl.formatMessage({ id: 'app.sml.promote.composer.needsMedia.attach', defaultMessage: 'Attach media' })}
      </SMLButton>
    </Alert>
  );
};

export const ComposerMediaWarning = TypedMemo(ComposerMediaWarningComponent);
